import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../i18n/config';
import { useLanguage } from '../hooks/useLanguage';
import logger from '../utils/logger';
import clsx from 'clsx';

interface LanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LanguageModal: React.FC<LanguageModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const { currentLanguage, isChanging, changeLanguage } = useLanguage();

  // Helper function to get language code without region
  const getLanguageCode = (lang: string): string => {
    return lang.split('-')[0];
  };

  // Force re-render when language changes
  useEffect(() => {
    // This is just to ensure the component re-renders when i18n.language changes
    logger.debug('LanguageModal - Current i18n language:', i18n.language);
    logger.debug('LanguageModal - Current i18n language code:', getLanguageCode(i18n.language));
    logger.debug('LanguageModal - Current Redux language:', currentLanguage);
    logger.debug('LanguageModal - Browser language:', navigator.language);
    logger.debug('LanguageModal - Browser language code:', getLanguageCode(navigator.language));
  }, [i18n.language, currentLanguage]);

  const handleLanguageSelect = async (langCode: string) => {
    logger.debug('LanguageModal - Language selected:', langCode);
    logger.debug('LanguageModal - Current language before change:', currentLanguage);
    
    // Compare normalized language codes
    if (getLanguageCode(langCode) === getLanguageCode(currentLanguage)) {
      logger.debug('LanguageModal - Same language selected, closing modal');
      onClose();
      return;
    }

    try {
      logger.debug('LanguageModal - Changing language to:', langCode);
      await changeLanguage(langCode);
      // Wait for a brief moment to ensure translations are loaded
      await new Promise(resolve => setTimeout(resolve, 300));
      logger.debug('LanguageModal - Language change complete, new language:', i18n.language);
      onClose();
    } catch (error) {
      logger.error('Failed to change language:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                  {t('common.selectLanguage')}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {Object.entries(SUPPORTED_LANGUAGES).map(([code, { nativeName }]) => (
                    <button
                      key={code}
                      onClick={() => handleLanguageSelect(code)}
                      disabled={isChanging || getLanguageCode(code) === getLanguageCode(currentLanguage)}
                      className={clsx(
                        'flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                        isChanging && 'opacity-50 cursor-not-allowed',
                        getLanguageCode(code) === getLanguageCode(currentLanguage)
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                      )}
                    >
                      {nativeName}
                      {getLanguageCode(code) === getLanguageCode(currentLanguage) && (
                        <span className="ml-2">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              disabled={isChanging}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 